import React, { useState } from 'react';
import { useMutation } from 'react-query';
import {
  Box, Typography, Button, Paper, TextField, Alert, Tooltip, IconButton, Stack,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ApiErrorAlert from '../../components/ApiErrorAlert';
import { issuePeerInvite } from '../../api/developerApi';

/**
 * "Invite peers" — an activated owner generates a redeemable invite link to share
 * (ADR-0053, #315). One capped link per inviter; re-generating refreshes the link
 * (and its expiry) while keeping the remaining-redemptions budget. The link is
 * shared out-of-band; redeemers self-select their email on the public /redeem
 * page. Requires an issued API key (the server returns 403 otherwise).
 */
export default function InvitePeersPage() {
  const [copied, setCopied] = useState(false);

  const redeemBase = `${window.location.origin}/redeem`;
  const mutation = useMutation(() => issuePeerInvite(redeemBase));
  const result = mutation.data;

  const handleCopy = (link) => {
    if (navigator.clipboard) navigator.clipboard.writeText(link);
    setCopied(true);
  };

  // A 403 means "not activated yet" (no API key) — give domain-specific guidance
  // rather than the generic error.
  const notActivated = mutation.isError && mutation.error?.response?.status === 403;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Invite peers</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 640 }}>
        Share Vectros with people you think should be in the preview. Generate a link
        and send it however you like — each person who opens it picks their own email
        and is admitted to sign up. Your link covers a limited number of invites.
      </Typography>

      <Paper sx={{ p: 3, maxWidth: 640 }}>
        {notActivated && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Create an API key first, then you can invite peers.
          </Alert>
        )}
        {mutation.isError && !notActivated && (
          <Box sx={{ mb: 2 }}><ApiErrorAlert error={mutation.error} /></Box>
        )}

        {!result && (
          <Button
            variant="contained"
            onClick={() => { setCopied(false); mutation.mutate(); }}
            disabled={mutation.isLoading}
            sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, fontWeight: 600 }}
          >
            {mutation.isLoading ? 'Generating…' : 'Generate invite link'}
          </Button>
        )}

        {result && (
          <Stack spacing={2}>
            <Alert severity="success" role="status">
              Your invite link is ready — {result.remaining} invite
              {result.remaining === 1 ? '' : 's'} remaining.
            </Alert>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                value={result.redeemLink || ''}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
                onFocus={(e) => e.target.select()}
              />
              <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                <IconButton aria-label="Copy invite link" onClick={() => handleCopy(result.redeemLink)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Anyone with this link can claim a spot until it&apos;s used up or expires.
              Re-generating makes a fresh link and invalidates the previous one.
            </Typography>
            <Box>
              <Button
                variant="outlined"
                onClick={() => { setCopied(false); mutation.mutate(); }}
                disabled={mutation.isLoading}
                sx={{ textTransform: 'none' }}
              >
                {mutation.isLoading ? 'Refreshing…' : 'Generate a fresh link'}
              </Button>
            </Box>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
